/**
 * Program Management API
 */

import { apiClient, ApiResponse, ApiError, ApiErrorResponse } from './client';

export interface Program {
  programId: string;
  programName: string;
  description: string;
  category: string;
  visibility?: 'public' | 'org-restricted';
  orgLeader?: string;
  orgLeaderName?: string;
  ownerId?: string;
  ownerName?: string;
  isEligible?: boolean;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  wikiUrl?: string;
  slackChannel?: string;
  admins?: ProgramAdmin[];
  cohortStatuses?: string[];
  totalCohortCount?: number;
}

export interface CreateProgramRequest {
  programName: string;
  description: string;
  category: string;
  wikiUrl?: string;
  slackChannel?: string;
  visibility?: 'public' | 'org-restricted';
  orgLeader?: string;
  orgLeaderName?: string;
  isPublished?: boolean;
}

export interface UpdateProgramRequest {
  programName?: string;
  description?: string;
  category?: string;
  wikiUrl?: string;
  slackChannel?: string;
  visibility?: 'public' | 'org-restricted';
  orgLeader?: string | null;
  orgLeaderName?: string | null;
  isPublished?: boolean;
}

export interface ProgramAdmin {
  userId: string;
  alias: string;
  name: string;
  email: string;
  grantedBy: string | null;
  grantedAt: number;
}

export interface CohortSummary {
  cohortId: string;
  cohortName: string;
  startDate: string;
  endDate: string;
  registrationStartDate?: string;
  registrationEndDate?: string;
  status: string;
  userIsMentor: boolean;
  userIsParticipant: boolean;
}

export interface ProgramForUser extends Program {
  isEligible: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  upcomingCohort?: CohortSummary;
  activeCohort?: CohortSummary;
  completedCohort?: CohortSummary;
}

export interface ProgramMetrics {
  programId: string;
  totalCohorts: number;
  activeCohorts: number;
  completedCohorts: number;
  totalParticipants: number;
  totalGraduated: number;
  graduationRate: number;
  avgCompletion: number;
}

/**
 * Program API Service
 */
export const programsApi = {
  /**
   * Create a new program
   */
  async create(data: CreateProgramRequest): Promise<Program> {
    const response = await apiClient.post<ApiResponse<Program>>('/programs', data);
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Get program by ID
   */
  async get(programId: string): Promise<Program> {
    const response = await apiClient.get<ApiResponse<Program>>(`/programs/${programId}`);
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Update program
   */
  async update(programId: string, data: UpdateProgramRequest): Promise<Program> {
    const response = await apiClient.put<ApiResponse<Program>>(`/programs/${programId}`, data);
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Delete program
   */
  async delete(programId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/programs/${programId}`);
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * List all programs (with optional search)
   */
  async list(search?: string): Promise<Program[]> {
    const path = search ? `/programs?search=${encodeURIComponent(search)}` : '/programs';
    const response = await apiClient.get<ApiResponse<Program[]>>(path);
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * List programs for specific user (optimized with cohort summaries and user relationships)
   */
  async listForUser(userId: string): Promise<ProgramForUser[]> {
    const response = await apiClient.get<ApiResponse<ProgramForUser[]>>(`/programs/user/${userId}`);
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Add program lead
   */
  async addLead(programId: string, userId: string): Promise<void> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/programs/${programId}/leads`,
      { userId }
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Remove program lead
   */
  async removeLead(programId: string, userId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/programs/${programId}/leads/${userId}`
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * List program leads
   */
  async listLeads(programId: string): Promise<string[]> {
    const response = await apiClient.get<ApiResponse<string[]>>(
      `/programs/${programId}/leads`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Get program admins
   */
  async getAdmins(programId: string): Promise<ProgramAdmin[]> {
    const response = await apiClient.get<ApiResponse<{ admins: ProgramAdmin[] }>>(
      `/programs/${programId}/admins`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data.admins;
  },

  /**
   * Add program admin
   */
  async addAdmin(programId: string, userId: string, alias: string, name?: string, email?: string): Promise<ProgramAdmin> {
    const response = await apiClient.post<ApiResponse<{ admin: ProgramAdmin }>>(
      `/programs/${programId}/admins`,
      { userId, alias, name, email }
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data.admin;
  },

  /**
   * Remove program admin
   */
  async removeAdmin(programId: string, userId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/programs/${programId}/admins/${userId}`
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Transfer program ownership to another user (manager only).
   * @param retainAsAdmin - when true (default) the outgoing owner is added as a
   *   program admin so they keep access; when false they leave the program entirely.
   */
  async transferOwnership(
    programId: string,
    newOwnerId: string,
    newOwnerName: string,
    retainAsAdmin: boolean = true,
  ): Promise<{ ownerId: string; ownerName: string }> {
    const response = await apiClient.put<ApiResponse<{ ownerId: string; ownerName: string }>>(
      `/programs/${programId}/owner`,
      { newOwnerId, newOwnerName, retainAsAdmin }
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },

  /**
   * Get program lifetime metrics
   */
  async getMetrics(programId: string): Promise<ProgramMetrics> {
    const response = await apiClient.get<ApiResponse<ProgramMetrics>>(
      `/programs/${programId}/metrics`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },

  /**
   * Publish a program (one-way, idempotent)
   */
  async publish(programId: string): Promise<{ programId: string; isPublished: boolean }> {
    const response = await apiClient.post<ApiResponse<{ programId: string; isPublished: boolean }>>(`/programs/${programId}/publish`, {});
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },
};
